import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Save, Percent, Cloud, Calculator, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["IDR", "USD", "EUR", "SGD", "MYR"];
const TIMEZONES = ["Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura"];
const INVENTORY_METHODS = ["FIFO", "Average", "LIFO"];
const OVERHEAD_METHODS = ["Kapasitas Normal", "Kapasitas Aktual", "Kapasitas Teoretis"];
const PERIODS = ["Bulanan", "Triwulanan", "Semesteran", "Tahunan"];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [saving, setSaving] = useState(false);
  const [logoErr, setLogoErr] = useState(false);
  const [form, setForm] = useState({
    company_code: "", company_name: "", business_type: "", nib: "", company_address: "",
    region: "", currency: "IDR", timezone: "Asia/Makassar", logo_url: "", status: "aktif",
    inventory_method: "FIFO", overhead_method: "Kapasitas Normal", normal_capacity: 1000,
    accounting_period: "Bulanan", default_margin: 20, carbon_tarif: 30000,
  });

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/settings")
      .then((r) => setForm((f) => ({ ...f, ...r.data })))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat pengaturan"));
  }, [isAdmin]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLogo = (v) => { setLogoErr(false); set("logo_url", v); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        company_code: form.company_code, company_name: form.company_name,
        business_type: form.business_type, nib: form.nib, company_address: form.company_address,
        region: form.region, currency: form.currency, timezone: form.timezone,
        logo_url: form.logo_url, status: form.status,
        inventory_method: form.inventory_method, overhead_method: form.overhead_method,
        normal_capacity: Number(form.normal_capacity),
        accounting_period: form.accounting_period,
        default_margin: Number(form.default_margin), carbon_tarif: Number(form.carbon_tarif),
      });
      toast.success("Pengaturan tersimpan");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-3xl">
        <Card className="p-8 border-slate-200 shadow-sm flex flex-col items-center text-center gap-3" data-testid="settings-access-denied">
          <ShieldAlert className="h-10 w-10 text-amber-500" />
          <h2 className="font-heading text-xl font-bold text-slate-900">Akses Terbatas</h2>
          <p className="text-sm text-slate-500 max-w-md">Hanya Super Admin yang dapat melihat dan mengubah pengaturan sistem. Silakan hubungi administrator Anda.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">Pengaturan Sistem</h2>
        <p className="text-sm text-slate-500 mt-1">Profil perusahaan, parameter akuntansi & costing.</p>
      </div>

      <Card className="p-6 border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-5"><Building2 className="h-5 w-5 text-green-700" /><h3 className="font-heading font-semibold">Profil Perusahaan</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Kode Perusahaan</Label>
            <Input value={form.company_code || ""} onChange={(e) => set("company_code", e.target.value)} data-testid="setting-company-code" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">NIB</Label>
            <Input value={form.nib || ""} onChange={(e) => set("nib", e.target.value)} data-testid="setting-nib" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Nama Perusahaan</Label>
            <Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="setting-company-name" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Jenis Usaha</Label>
            <Input value={form.business_type || ""} onChange={(e) => set("business_type", e.target.value)} data-testid="setting-business-type" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Alamat</Label>
            <Input value={form.company_address || ""} onChange={(e) => set("company_address", e.target.value)} data-testid="setting-company-address" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Wilayah</Label>
            <Input value={form.region || ""} onChange={(e) => set("region", e.target.value)} data-testid="setting-region" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Mata Uang</Label>
            <Select value={form.currency || "IDR"} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="setting-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Zona Waktu</Label>
            <Select value={form.timezone || "Asia/Makassar"} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger data-testid="setting-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">URL Logo</Label>
            <Input value={form.logo_url || ""} onChange={(e) => setLogo(e.target.value)} placeholder="https://..." data-testid="setting-logo-url" /></div>
        </div>
        {form.logo_url ? (
          <div className="mt-4"><Label className="text-xs font-semibold text-slate-600">Pratinjau Logo</Label>
            {logoErr ? (
              <p className="text-xs text-amber-600 mt-2" data-testid="setting-logo-error">URL logo tidak dapat dimuat.</p>
            ) : (
              <img key={form.logo_url} src={form.logo_url} alt="Logo" className="h-16 mt-2 rounded border border-slate-200 object-contain bg-white p-1"
                onError={() => setLogoErr(true)} onLoad={() => setLogoErr(false)} data-testid="setting-logo-preview" />
            )}
          </div>
        ) : null}
      </Card>

      <Card className="p-6 border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-5"><Calculator className="h-5 w-5 text-green-700" /><h3 className="font-heading font-semibold">Parameter Akuntansi & Costing</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Metode Persediaan</Label>
            <Select value={form.inventory_method || "FIFO"} onValueChange={(v) => set("inventory_method", v)}>
              <SelectTrigger data-testid="setting-inventory-method"><SelectValue /></SelectTrigger>
              <SelectContent>{INVENTORY_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Metode Overhead</Label>
            <Select value={form.overhead_method || "Kapasitas Normal"} onValueChange={(v) => set("overhead_method", v)}>
              <SelectTrigger data-testid="setting-overhead-method"><SelectValue /></SelectTrigger>
              <SelectContent>{OVERHEAD_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Kapasitas Normal</Label>
            <Input type="number" min="1" value={form.normal_capacity} onChange={(e) => set("normal_capacity", e.target.value)} data-testid="setting-normal-capacity" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Periode Akuntansi</Label>
            <Select value={form.accounting_period || "Bulanan"} onValueChange={(v) => set("accounting_period", v)}>
              <SelectTrigger data-testid="setting-accounting-period"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Margin Default (%)</Label>
            <Input type="number" min="0" value={form.default_margin} onChange={(e) => set("default_margin", e.target.value)} data-testid="setting-margin" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Cloud className="h-3.5 w-3.5" /> Tarif Karbon (Rp/ton CO₂e)</Label>
            <Input type="number" min="0" value={form.carbon_tarif} onChange={(e) => set("carbon_tarif", e.target.value)} data-testid="setting-carbon" /></div>
        </div>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-green-700 hover:bg-green-800" data-testid="settings-save-btn">
        {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Menyimpan...</> : <><Save className="h-4 w-4 mr-1.5" /> Simpan Pengaturan</>}
      </Button>
    </div>
  );
}
